import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Play } from 'lucide-react';
import { heroData } from '../mockData';

export const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
      {/* Video Background */}
      <div className="absolute inset-0">
        <iframe
          src="https://iframe.mediadelivery.net/embed/604767/c227f5d5-3c38-433d-8105-593f6095817a?autoplay=true&loop=true&muted=true&preload=true"
          loading="lazy"
          style={{ border: 0, position: 'absolute', top: '50%', left: '50%', width: 'max(177.78vh, 100vw)', height: 'max(100vh, 56.25vw)', transform: 'translate(-50%, -50%)' }}
          allow="accelerometer;gyroscope;autoplay;encrypted-media;picture-in-picture;"
          allowFullScreen={true}
          title="Hero Background Video"
        />
        <div className="absolute inset-0 bg-black/55"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        {/* Logo mark */}
        <div className="flex justify-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_axicle-robotics/artifacts/uz6ua6if_Logo.svg" 
            alt="Axicle Logo" 
            className="h-24 sm:h-32 md:h-40 w-auto animate-fade-in"
          />
        </div>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
          {heroData.headline}
        </h1>

        {/* Subheadline with red accent */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className="h-px w-12 bg-red-600"></div>
          <p className="text-xl sm:text-2xl md:text-3xl font-semibold text-red-500">
            {heroData.subheadline}
          </p>
          <div className="h-px w-12 bg-red-600"></div>
        </div>

        {/* Description */}
        <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto mb-12 leading-relaxed">
          Axicle's <span className="text-red-500 font-semibold">Robotic Coupling System</span> revolutionizes trucking operations with automatic trailer coupling in 30 seconds, preventing rollovers and saving $17,500 per truck annually.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button 
            onClick={() => scrollToSection('contact')}
            size="lg"
            className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-semibold transition-all duration-200 hover:scale-105 group"
          >
            {heroData.ctaText}
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Button>
          <Button 
            onClick={() => scrollToSection('video')}
            size="lg"
            variant="outline"
            className="border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 px-8 py-6 text-lg font-semibold transition-all duration-200"
          >
            <Play className="mr-2" size={20} />
            {heroData.secondaryCtaText}
          </Button>
        </div>

        {/* Quick stats */}
        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
          <div className="text-center">
            <div className="text-4xl font-bold text-red-500 mb-2">30s</div>
            <div className="text-sm text-gray-400 uppercase tracking-wider">Coupling Time</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-red-500 mb-2">$17.5K</div>
            <div className="text-sm text-gray-400 uppercase tracking-wider">Annual Savings</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-red-500 mb-2">5%</div>
            <div className="text-sm text-gray-400 uppercase tracking-wider">Fuel Efficiency</div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-2">
          <div className="w-1 h-3 bg-red-500 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};
